import type { AssetListItem } from '@cartwhl/common/api/getAssets';

export const ASSET_LIBRARY_GROUP_ID_PREFIX = {
  project: 'project-',
  comicBatch: 'batch-comic-',
  swingBatch: 'batch-generation-',
  scene: 'scene-',
} as const;

export const PROJECT_HISTORY_PAGE_SIZE = 20;

export function isProjectGroupID(groupID: string): boolean {
  return groupID.startsWith(ASSET_LIBRARY_GROUP_ID_PREFIX.project);
}

export function isComicBatchGroupID(groupID: string): boolean {
  return groupID.startsWith(ASSET_LIBRARY_GROUP_ID_PREFIX.comicBatch);
}

export function isSwingBatchGroupID(groupID: string): boolean {
  return groupID.startsWith(ASSET_LIBRARY_GROUP_ID_PREFIX.swingBatch);
}

export function isMogenBatchGroupID(groupID: string): boolean {
  return isComicBatchGroupID(groupID) || isSwingBatchGroupID(groupID);
}

/** Batch/project groups bookmark via groupID rather than individual job asset IDs. */
export function isGroupLevelBookmarkGroupID(groupID: string): boolean {
  return isProjectGroupID(groupID) || isMogenBatchGroupID(groupID);
}

/** Comic batches show a source-video overlay in the motion preview modal. */
export function isComicMogenGroupID(groupID: string): boolean {
  return isComicBatchGroupID(groupID);
}

/** Raw batch ID for export/download endpoints (matches comic/swing app pages). */
export function resolveMogenBatchExportId(groupID: string): string {
  if (isComicBatchGroupID(groupID)) {
    return groupID.slice(ASSET_LIBRARY_GROUP_ID_PREFIX.comicBatch.length);
  }

  if (isSwingBatchGroupID(groupID)) {
    return groupID.slice(ASSET_LIBRARY_GROUP_ID_PREFIX.swingBatch.length);
  }

  return groupID;
}

export function getMogenJobDisplayTypeFromGroupID(
  groupID: string | undefined,
): string {
  if (!groupID) {
    return 'Motion';
  }

  if (isComicBatchGroupID(groupID)) {
    return 'Performance Capture';
  }

  if (isProjectGroupID(groupID)) {
    return 'Canvas project';
  }

  if (isSwingBatchGroupID(groupID)) {
    return 'Performance Generation';
  }

  if (groupID.startsWith(ASSET_LIBRARY_GROUP_ID_PREFIX.scene)) {
    return 'Cartwheel scene';
  }

  return 'Motion';
}

export function isGroupBookmarked(
  groupID: string,
  referenceAssets: readonly AssetListItem[],
): boolean {
  return referenceAssets.some(
    (item) =>
      item.groupID === groupID &&
      Boolean(item.isBookmarked) &&
      !item.isDeleted,
  );
}

/** Stacked batch/project cards may need a sibling list to resolve group bookmark state. */
export function resolveAssetBookmarkedState(
  asset: AssetListItem & { groupItemCount?: number },
  referenceAssets: readonly AssetListItem[] = [],
): boolean {
  if ((asset.groupItemCount ?? 0) <= 1 || !asset.groupID) {
    return Boolean(asset.isBookmarked);
  }

  return isGroupBookmarked(asset.groupID, referenceAssets);
}
